/**
 * Unified Auth Storage Utility
 * Reads auth data (accessToken, refreshToken) from persist:userdb (Redux persisted state).
 * Writes auth data only via Redux store; Redux persist saves to persist:userdb.
 */

import { store } from "../../store";
import { SET_AUTH_DATA } from "../../db/actionTypes/otpValidation";

/** localStorage key for Redux persisted state (contains authDetails, profileInformation, etc.) */
const PERSIST_USERDB_KEY = "persist:userdb";

export interface AuthData {
  accessToken: string;
  refreshToken: string;
  sessionId?: string;
  activeRole?: string;
  availableRoles?: string[];
  user?: {
    firstname?: string;
    lastname?: string;
    [key: string]: any;
  };
}

/**
 * Parsed auth slice from persist:userdb (authDetails JSON).
 */
interface PersistedAuthDetails {
  auth?: {
    accessToken?: string;
    refreshToken?: string;
    sessionId?: string;
    activeRole?: string;
    availableRoles?: string[];
    user?: Record<string, unknown>;
  };
}

/**
 * Stores auth data in Redux store only. Redux persist will persist to persist:userdb.
 * @param authData - The auth data to store
 */
export function storeAuthData(authData: AuthData): void {
  try {
    store.dispatch({
      type: SET_AUTH_DATA,
      payload: {
        accessToken: authData.accessToken,
        refreshToken: authData.refreshToken,
        sessionId: authData.sessionId,
        activeRole: authData.activeRole,
        availableRoles: authData.availableRoles,
        user: authData.user || {},
      },
    });

    console.log("🟢 storeAuthData: Stored auth data in Redux store (persist:userdb)", {
      hasAccessToken: !!authData.accessToken,
      hasRefreshToken: !!authData.refreshToken,
      hasSessionId: !!authData.sessionId,
      sessionId: authData.sessionId,
      hasUser: !!authData.user,
    });
  } catch (error) {
    console.error("🔴 storeAuthData: Error storing auth data", error);
    throw error;
  }
}

/**
 * Gets auth data from persist:userdb (parses authDetails.auth for accessToken, refreshToken).
 * @returns Auth data or null if not found
 */
export function getAuthDataFromPersistUser(): AuthData | null {
  try {
    const raw = localStorage.getItem(PERSIST_USERDB_KEY);
    if (!raw) return null;

    const state = JSON.parse(raw) as { authDetails?: string };
    const authDetailsStr = state.authDetails;
    if (!authDetailsStr) return null;

    const authDetails = JSON.parse(authDetailsStr) as PersistedAuthDetails;
    const auth = authDetails?.auth;
    if (!auth) return null;

    return {
      accessToken: auth.accessToken ?? "",
      refreshToken: auth.refreshToken ?? "",
      sessionId: auth.sessionId,
      activeRole: auth.activeRole,
      availableRoles: auth.availableRoles,
      user: (auth.user as AuthData["user"]) || {},
    };
  } catch (error) {
    console.error("🔴 getAuthDataFromPersistUser: Error reading from persist:userdb", error);
  }
  return null;
}

/**
 * Clears auth data from Redux store only. persist:userdb is updated by Redux persist.
 * Caller should dispatch logout/clear auth actions; this is a no-op for localStorage.
 */
export function clearAuthData(): void {
  // persist:userdb is managed by Redux persist; auth is cleared via store dispatch
  console.log("🟢 clearAuthData: Auth clear is handled via Redux store dispatch");
}

/** Token data used when updating persist:userdb auth. */
export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  sessionId?: string;
}

/**
 * Gets access token, refresh token, role, and sessionId.
 * Reads from the Redux store first (synchronous), then falls back to
 * persist:userdb in localStorage. The Redux-first strategy prevents a
 * race condition where storeAuthData() has dispatched to the store but
 * Redux Persist has not yet flushed the new token to localStorage.
 */
export function getAccessAndRefreshToken(): {
  accessToken: string | null;
  refreshToken: string | null;
  role: string | null;
  sessionId: string | null;
} {
  // Prefer the in-memory Redux store (updated synchronously by storeAuthData).
  try {
    const state = store.getState() as {
      authDetails?: {
        auth?: {
          accessToken?: string;
          refreshToken?: string;
          sessionId?: string;
          activeRole?: string;
        };
      };
    };
    const reduxAuth = state?.authDetails?.auth;
    if (reduxAuth?.accessToken && reduxAuth.accessToken.trim() !== "") {
      const sessionId =
        reduxAuth.sessionId && String(reduxAuth.sessionId).trim()
          ? reduxAuth.sessionId
          : null;
      return {
        accessToken: reduxAuth.accessToken,
        refreshToken: reduxAuth.refreshToken || null,
        role: reduxAuth.activeRole || null,
        sessionId,
      };
    }
  } catch {
    // Fall through to localStorage if Redux read fails
  }

  // Fallback: read from persist:userdb in localStorage.
  const auth = getAuthDataFromPersistUser();
  if (!auth) return { accessToken: null, refreshToken: null, role: null, sessionId: null };
  const sessionId =
    auth.sessionId && String(auth.sessionId).trim() ? auth.sessionId : null;
  return {
    accessToken: auth.accessToken || null,
    refreshToken: auth.refreshToken || null,
    role: auth.activeRole || null,
    sessionId,
  };
}

/**
 * Updates only the auth tokens inside persist:userdb (authDetails.auth).
 * Does not change the top-level structure: authDetails, profileInformation, _persist.
 */
export function updatePersistUserDbAuthTokens(tokens: AuthTokens): void {
  try {
    const raw = localStorage.getItem(PERSIST_USERDB_KEY);
    if (!raw) return;
    const outer = JSON.parse(raw) as Record<string, unknown>;
    if (!outer || typeof outer.authDetails !== "string") return;
    const authDetails = JSON.parse(outer.authDetails as string) as Record<string, unknown>;
    if (!authDetails || typeof authDetails.auth !== "object" || authDetails.auth === null) return;
    const auth = authDetails.auth as Record<string, unknown>;
    auth.accessToken = tokens.accessToken;
    auth.refreshToken = tokens.refreshToken;
    if (tokens.sessionId !== undefined) auth.sessionId = tokens.sessionId;
    outer.authDetails = JSON.stringify(authDetails);
    localStorage.setItem(PERSIST_USERDB_KEY, JSON.stringify(outer));
  } catch (err) {
    console.error("Error updating persist:userdb auth tokens:", err);
  }
}
